/**
 * Help Center locale segment keyed by app locale. The Help Center publishes
 * English and French only, and an unpublished segment (e.g. `/de/`) drops the
 * visitor on the home page instead of the article, so everything else points
 * at `en`.
 */
const HELP_CENTER_LOCALE = {
  "en-US": "en",
  "fr-FR": "fr",
  "de-DE": "en",
  "es-ES": "en",
  "en-AU": "en",
  "en-CA": "en",
  "en-GB": "en",
} as const;

const DEFAULT_HELP_CENTER_LOCALE = HELP_CENTER_LOCALE["en-US"];

/**
 * Legacy Zendesk locale segment, still used by `/hc/` support links that have
 * not moved to the current Help Center.
 */
const ZENDESK_LOCALE = {
  "en-US": "en-us",
  "fr-FR": "fr",
  "de-DE": "en-us",
  "es-ES": "en-us",
  "en-AU": "en-us",
  "en-CA": "en-us",
  "en-GB": "en-us",
} as const;

const DEFAULT_ZENDESK_LOCALE = ZENDESK_LOCALE["en-US"];

/**
 * Resolves the Help Center language segment for a viewer's locale
 * (`fr-FR` → `fr`), falling back to `en` for locales it does not publish.
 */
const getHelpCenterLocale = (locale?: string): string =>
  HELP_CENTER_LOCALE[locale as keyof typeof HELP_CENTER_LOCALE] ??
  DEFAULT_HELP_CENTER_LOCALE;

/** Resolves the legacy Zendesk locale segment for a viewer's locale. */
const getZendeskLocale = (locale?: string): string =>
  ZENDESK_LOCALE[locale as keyof typeof ZENDESK_LOCALE] ??
  DEFAULT_ZENDESK_LOCALE;

/**
 * Builds a Help Center article URL localized for the viewer, e.g.
 * `https://support.peerspace.com/fr/articles/10119157-what-is-…`. The Help
 * Center resolves the article by its leading id, so the English slug still
 * lands on the translated article.
 */
const getSupportArticleUrl = (article: string, locale?: string): string =>
  `https://support.peerspace.com/${getHelpCenterLocale(locale)}/articles/${article}`;

export {
  getHelpCenterLocale,
  getSupportArticleUrl,
  getZendeskLocale,
  HELP_CENTER_LOCALE,
  ZENDESK_LOCALE,
};
